import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { createEventEmitter } from '@nzyme/utils/createEventEmitter.js';
import { storageRefWithSchema } from '@nzyme/vue-utils/reactivity/storageRefWithSchema.js';
import { setUser } from '@sentry/nuxt';
import { decodeJwt } from 'jose';
import { computed, shallowRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import * as z from 'zod/mini';
import { AppConfig } from '@sigma/common/AppConfig.js';
import { getLocaleLanguage } from '@sigma/common/i18n/getLocaleLanguage.js';
import { PRODUCTION } from '@sigma/env/environments.js';
import { COGNITO_ID_CLAIM, COGNITO_LOCALE_CLAIM } from '@sigma/env/resources.js';
import { AuthState } from './AuthStore.js';
/**
 * How long before the real token expiration a token is already treated as expired. Refreshing early
 * means an in-flight request never carries a token that expires mid-flight.
 */
const AUTH_EXPIRATION_SKEW_MS = 5 * 60_000;
/**
 * Context-agnostic half of the auth state, shared by every app's auth store. It owns the durable
 * snapshot, the published snapshot, the derived user, the auth events and the teardown — everything
 * that does not depend on whether the user is a patient or a staff member. Each app's store adds its
 * own sign-in/refresh/logout lifecycle on top.
 *
 * There is ONE snapshot, in local storage, exposed through two views:
 * - `stored` is the raw persisted snapshot, whether or not the owning store trusts it yet.
 * - `auth` is the same snapshot as consumers see it — hidden while the store has `quarantine`d it.
 *
 * Whether to quarantine is the store's policy, and it is the whole difference between the patient
 * store (a snapshot vouches for itself, so it is live immediately) and the staff store (nothing is
 * trusted until Cognito confirms it). Keeping that decision in the store is what lets this service
 * stay free of user-type branches. Deriving `auth` from storage rather than copying it is what keeps
 * a sign-in or sign-out in another browser tab propagating to this one.
 *
 * `setup()` returns a PLAIN object of refs, never a `reactive({...})` wrapper. The stores assign
 * these members straight into their own `reactive({ auth: session.auth, ... })`; a `reactive()`
 * wrapper here would unwrap each ref to a plain value at setup time, so every consumer would read a
 * snapshot frozen at startup — with no type error anywhere. Read them as `.value` inside this file.
 */
export const AuthSession = defineService({
    name: 'AuthSession',
    deps: {
        appConfig: AppConfig,
        logger: Logger,
    },
    setup({ appConfig, logger }) {
        const eventAuthenticated = createEventEmitter();
        const eventUnauthenticated = createEventEmitter();
        const eventUserChanged = createEventEmitter();
        const router = useRouter();
        const storedRef = storageRefWithSchema({
            key: `${appConfig.name}:auth`,
            sync: 'always',
            schema: z.nullable(AuthState),
            default: () => null,
        });
        // Whether the owning store still distrusts the stored snapshot. Kept as a gate *over* the
        // stored ref rather than as a second copy of the snapshot: the storage ref syncs across
        // browser tabs (`sync: 'always'`), so `auth` must stay derived from it. A standalone
        // published ref would go stale the moment another tab signed in or out — a tab would keep
        // presenting a bearer token for a session that was already ended elsewhere.
        const quarantinedRef = shallowRef(false);
        const authRef = computed(() => (quarantinedRef.value ? null : storedRef.value));
        const jwtRef = computed(() => {
            const token = authRef.value?.idToken;
            if (!token) {
                return null;
            }
            return decodeJwt(token);
        });
        const userRef = computed(() => {
            const jwt = jwtRef.value;
            if (!jwt) {
                return null;
            }
            const id = jwt[COGNITO_ID_CLAIM];
            const sub = jwt.sub;
            if (!id || !sub) {
                return null;
            }
            const locale = jwt[COGNITO_LOCALE_CLAIM];
            const user = {
                id,
                sub,
                email: jwt.email,
                phone: jwt.phone_number,
                firstName: jwt.given_name,
                lastName: jwt.family_name,
                photo: jwt.picture,
                language: getLocaleLanguage(typeof locale === 'string' ? locale : undefined),
                claims: jwt,
            };
            return user;
        });
        // Fires `userChanged` on every id transition — login, logout, or account
        // switch. Subscribers (e.g. per-user caches that need to wipe stale data)
        // register with `events.userChanged.on(...)` and don't have to compare ids
        // themselves; Vue's `watch` only invokes this callback when the id actually
        // changes.
        watch(() => userRef.value?.id, (newUserId, oldUserId) => {
            eventUserChanged.emit({ newUserId, oldUserId });
        });
        if (!PRODUCTION) {
            watch(storedRef, auth => logger.info('Auth updated', {
                hasAuth: Boolean(auth),
                userId: userRef.value?.id,
            }));
        }
        return {
            /**
             * The durable snapshot as persisted in local storage, regardless of whether the owning
             * store trusts it yet. Read-only on purpose: a snapshot may only be written through
             * {@link setAuth} or dropped through {@link clear}. Stores read it to reason about a
             * restored session they have not verified.
             */
            stored: computed(() => storedRef.value),
            /**
             * The snapshot consumers see: the stored one, unless the owning store has
             * {@link quarantine}d it. Because it stays *derived* from storage, a sign-in or sign-out
             * in another browser tab propagates here automatically.
             */
            auth: authRef,
            /** The user derived from {@link auth}, so a quarantined session has no user. */
            user: userRef,
            events: {
                authenticated: eventAuthenticated.event,
                unauthenticated: eventUnauthenticated.event,
                userChanged: eventUserChanged.event,
            },
            setAuth,
            quarantine,
            announceUnlessStale,
            notifyAuthenticated,
            notifySignedIn,
            clear,
            isExpired,
        };
        /**
         * The freshness gate every auth store runs on each periodic check: announces the published
         * session while it is still usable, and tells the caller when it is not, so the caller can
         * renew it the way its identity provider requires.
         *
         * Announcing and classifying are one step on purpose — both stores need exactly this pair,
         * and separating them would only make each of them re-pair the halves identically.
         *
         * @returns `true` only when a session exists but has gone stale, i.e. the caller must renew
         * it. A quarantined session reports `false`, indistinguishable from having none — which is
         * what a store that has not yet verified its restored snapshot relies on.
         */
        async function announceUnlessStale() {
            const auth = authRef.value;
            if (!auth) {
                return false;
            }
            if (isExpired(auth)) {
                return true;
            }
            await notifyAuthenticated();
            return false;
        }
        /**
         * Persists a verified snapshot and lifts any quarantine — a store only calls this once it
         * trusts the session. Emits nothing; the caller decides when to announce.
         */
        function setAuth(auth) {
            storedRef.value = auth;
            quarantinedRef.value = false;
        }
        /**
         * Hides the stored snapshot from consumers until the owning store verifies it. A store whose
         * sessions are self-describing (the snapshot carries its own refresh token) never calls this
         * and its restored session is live immediately; a store that must confirm the session against
         * an external identity provider calls it at setup and again whenever it drops the session.
         */
        function quarantine() {
            quarantinedRef.value = true;
        }
        /**
         * Announces the currently published user to subscribers. Called on every session check, so it
         * stays side-effect free on purpose — see {@link notifySignedIn} for the sign-in-only extras.
         */
        async function notifyAuthenticated() {
            await eventAuthenticated.emitAsync(userRef.value);
        }
        /**
         * Announces a *new* sign-in: the same event as {@link notifyAuthenticated}, plus the one-off
         * bookkeeping that must not repeat on every periodic check — tagging the error reporter with
         * the user and recording the sign-in in the log.
         */
        async function notifySignedIn() {
            await notifyAuthenticated();
            const user = userRef.value;
            if (!user) {
                return;
            }
            setUser({
                id: user.id.toString(),
            });
            logger.info('Logged in', { userId: user.id });
        }
        /**
         * Full teardown of the session. The order matters: the snapshot is dropped BEFORE
         * `unauthenticated` is emitted, so no subscriber can read a stale user, and the router is
         * refreshed last so route guards re-evaluate against the cleared state. Dropping the stored
         * snapshot also signs the user out of every other tab, since the storage ref is synced.
         *
         * The quarantine flag is deliberately left alone: whether a *future* restored snapshot may be
         * trusted is the owning store's policy, not the teardown's — a store that quarantines calls
         * {@link quarantine} itself after clearing.
         */
        async function clear() {
            storedRef.value = null;
            setUser(null);
            await eventUnauthenticated.emitAsync();
            await router.push({
                path: router.currentRoute.value.path,
                force: true,
            });
        }
        /**
         * Whether a snapshot's id token is expired, counting {@link AUTH_EXPIRATION_SKEW_MS} of skew.
         * Takes the snapshot explicitly because stores ask about both the published one ("do I need to
         * refresh?") and the stored one ("was the restored session already stale?"). A missing snapshot
         * or a token without an expiration claim is reported as not expired — there is nothing to
         * refresh.
         */
        function isExpired(auth) {
            const token = auth?.idToken;
            if (!token) {
                return false;
            }
            const expiration = decodeJwt(token).exp;
            if (!expiration) {
                return false;
            }
            return expiration * 1000 - AUTH_EXPIRATION_SKEW_MS < Date.now();
        }
    },
});
//# sourceMappingURL=AuthSession.js.map